import { useCallback, useEffect, useState } from 'react';

export type ThemePref = 'system' | 'light' | 'dark';
export const THEME_KEY = 'facturita.theme';
export const THEMES: ThemePref[] = ['system', 'light', 'dark'];

/**
 * Accent palettes. Each defines light + dark token sets in src/index.css.
 *
 * A palette carries two roles of equal standing: `accent` is what you ACT on (buttons, the
 * active nav item, links) and `alt` is what the app REPORTS (the plan card, the trend, the
 * income pill, the business identity). Single-hue palettes have no second accent, so `alt`
 * is null and the picker draws a solid dot.
 */
export const PALETTES = ['default', 'menta', 'sepia', 'ororosa', 'oliva', 'corporativo', 'chicle', 'brasa'] as const;
export type Palette = (typeof PALETTES)[number];
export const PALETTE_KEY = 'facturita.palette';

export interface PaletteSwatch {
  accent: string;
  /** null = single hue. */
  alt: string | null;
  bg: string;
}

/** Picker swatches, mirroring index.css. */
export const PALETTE_SWATCH: Record<Palette, { light: PaletteSwatch; dark: PaletteSwatch }> = {
  default: {
    light: { accent: '#0F62FE', alt: null, bg: '#f4f4f4' },
    dark: { accent: '#5B8DEF', alt: null, bg: '#141414' },
  },
  menta: {
    light: { accent: '#00A389', alt: null, bg: '#eff8f6' },
    dark: { accent: '#34D399', alt: null, bg: '#0d1613' },
  },
  sepia: {
    light: { accent: '#A9744F', alt: null, bg: '#f8f3ef' },
    dark: { accent: '#D2A679', alt: null, bg: '#16120d' },
  },
  ororosa: {
    light: { accent: '#A85462', alt: '#8A6E45', bg: '#f8eff1' },
    dark: { accent: '#E9A0AB', alt: '#D3B279', bg: '#160d0f' },
  },
  oliva: {
    light: { accent: '#5F7326', alt: '#3E6A8A', bg: '#f6f8ef' },
    dark: { accent: '#A8C24E', alt: '#7FB2D4', bg: '#14160d' },
  },
  corporativo: {
    light: { accent: '#1585C4', alt: '#64798C', bg: '#eff5f8' },
    dark: { accent: '#4FC3E8', alt: '#9EAFBE', bg: '#0d1416' },
  },
  chicle: {
    light: { accent: '#D6337F', alt: '#1189C9', bg: '#f8eff3' },
    dark: { accent: '#FF8FC2', alt: '#7FC8F0', bg: '#160d11' },
  },
  brasa: {
    light: { accent: '#C7381A', alt: '#A06908', bg: '#f8f1ef' },
    dark: { accent: '#F2643F', alt: '#D1951F', bg: '#160f0d' },
  },
};

/** The second accent for `p`, or null when the palette is a single hue. */
export function swatchAltFor(p: Palette, mode: 'light' | 'dark'): string | null {
  return PALETTE_SWATCH[p][mode].alt;
}

const mq = () => (typeof window !== 'undefined' && window.matchMedia ? window.matchMedia('(prefers-color-scheme: dark)') : null);

export function getThemePref(): ThemePref {
  try {
    const v = localStorage.getItem(THEME_KEY);
    return v === 'light' || v === 'dark' ? v : 'system';
  } catch {
    return 'system';
  }
}

export function getPalette(): Palette {
  try {
    const v = localStorage.getItem(PALETTE_KEY);
    return (PALETTES as readonly string[]).includes(v ?? '') ? (v as Palette) : 'default';
  } catch {
    return 'default';
  }
}

export function resolveTheme(pref: ThemePref): 'light' | 'dark' {
  if (pref === 'system') return mq()?.matches ? 'dark' : 'light';
  return pref;
}

/** Stamp data-theme / data-palette on <html>. The inline script in index.html does the same before first paint. */
export function applyTheme(pref: ThemePref, palette: Palette = getPalette()) {
  const root = document.documentElement;
  const resolved = resolveTheme(pref);
  root.setAttribute('data-theme', resolved);
  if (palette === 'default') root.removeAttribute('data-palette');
  else root.setAttribute('data-palette', palette);
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) {
    const primary = getComputedStyle(root).getPropertyValue('--color-primary').trim();
    meta.setAttribute('content', resolved === 'dark' ? '#141414' : primary || '#0F62FE');
  }
}

export function useTheme() {
  const [pref, setPrefState] = useState<ThemePref>(getThemePref);
  const [palette, setPaletteState] = useState<Palette>(getPalette);

  useEffect(() => {
    applyTheme(pref, palette);
    if (pref !== 'system') return;
    const m = mq();
    if (!m) return;
    const onChange = () => applyTheme('system', palette);
    m.addEventListener('change', onChange);
    return () => m.removeEventListener('change', onChange);
  }, [pref, palette]);

  const setPref = useCallback((p: ThemePref) => {
    setPrefState(p);
    try {
      if (p === 'system') localStorage.removeItem(THEME_KEY);
      else localStorage.setItem(THEME_KEY, p);
    } catch {
      /* ignore */
    }
  }, []);

  const setPalette = useCallback((p: Palette) => {
    setPaletteState(p);
    try {
      if (p === 'default') localStorage.removeItem(PALETTE_KEY);
      else localStorage.setItem(PALETTE_KEY, p);
    } catch {
      /* ignore */
    }
  }, []);

  return { pref, setPref, palette, setPalette, resolved: resolveTheme(pref) };
}
